/**
 * Copy for in-flow progressive onboarding — shown in modal, not full-page auth walls.
 */

export type ProgressiveAuthIntent =
  | 'postListing'
  | 'claimGig'
  | 'inquireListing'
  | 'runnerAction'
  | 'townHall'
  | 'merchantGig'
  | 'syncJobs'
  | 'settleWallet'
  | 'general';

export type ProgressiveAuthIntentConfig = {
  title: string;
  body: string;
  /** Wallet-only path is enough (no Sharetribe email account required). */
  walletOnlyOk: boolean;
  defaultTab: 'login' | 'signup' | 'wallet';
  userType?: string;
};

export const progressiveAuthIntents: Record<ProgressiveAuthIntent, ProgressiveAuthIntentConfig> = {
  postListing: {
    title: 'Save your listing',
    body: 'Marketplace listings publish through a free email account. Your connected wallet stays linked for escrow and payouts.',
    walletOnlyOk: false,
    defaultTab: 'signup',
  },
  claimGig: {
    title: 'Claim this gig',
    body: 'Sign in with email or connect your wallet to claim runner payouts on-chain.',
    walletOnlyOk: true,
    defaultTab: 'signup',
    userType: 'provider',
  },
  inquireListing: {
    title: 'Send your inquiry',
    body: 'Sign up or log in with email to message the seller. Settlement happens on external rails when you are ready.',
    walletOnlyOk: false,
    defaultTab: 'signup',
  },
  runnerAction: {
    title: 'Continue as runner',
    body: 'Sign in or connect a wallet to confirm handoffs and release escrow.',
    walletOnlyOk: true,
    defaultTab: 'login',
    userType: 'provider',
  },
  townHall: {
    title: 'Join the conversation',
    body: 'Post to Town Hall with an account — helps us keep bots and junk off the feed.',
    walletOnlyOk: false,
    defaultTab: 'signup',
  },
  merchantGig: {
    title: 'Post to the noticeboard',
    body: 'Connect your merchant wallet to sign and publish gigs. No password needed.',
    walletOnlyOk: true,
    defaultTab: 'signup',
  },
  syncJobs: {
    title: 'Sync your requests',
    body: 'Optional — keep jobs across devices. Email account or wallet session both work. You can stay on this device without signing in.',
    walletOnlyOk: true,
    defaultTab: 'wallet',
  },
  settleWallet: {
    title: 'Connect wallet to settle',
    body: 'Crypto settlement is wallet-first — no email required. Cash + handoff PIN stays available without a wallet.',
    walletOnlyOk: true,
    defaultTab: 'wallet',
  },
  general: {
    title: 'Continue',
    body: 'Sign in with email and password, or connect your Web3 wallet.',
    walletOnlyOk: true,
    defaultTab: 'login',
  },
};

export const progressiveAuthShellCopy = {
  loginTab: 'Log in',
  signupTab: 'Sign up',
  walletTab: 'Wallet Connect',
  dismiss: 'Not now',
  fullPageLink: 'Open full sign-in page',
  honeypotLabel: 'Leave blank',
  protectionNote: 'Protected by rate limits and wallet signatures under the hood.',
  walletConnectedNeedsEmail:
    'Your wallet is already connected. Sign up or log in with email to publish marketplace listings.',
};
